import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useParams } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";

const resolveItemFromResponse = (response) => {
  if (!response) return null;
  if (response.item) return response.item;
  if (Array.isArray(response.items)) return response.items[0] || null;
  return response;
};

const extractSentenceTokens = (sentence) =>
  typeof sentence === "string"
    ? sentence.trim().split(/\s+/).filter(Boolean)
    : [];

const createEmptyOption = (id) => ({
  id,
  Value: "",
  Rationale: "",
});

const createEmptyItem = () => ({
  Type: "ReArrange",
  Name: "",
  Title: "",
  Direction: "LTR",
  points: 1,
  skilltype: "",
  itemcat: "",
  Options: [createEmptyOption(1)],
});

const ReArrangeItemDetail = () => {
  const { itemId } = useParams();
  const isNew = !itemId || itemId === "new";

  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(createEmptyItem());
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);

  // 🆕 Preview dialog state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  const [previewOrder, setPreviewOrder] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!isNew) {
          const data = await ItemService.fetchItemById(itemId);
          const resolved = resolveItemFromResponse(data);
          setItem({
            ...resolved,
            skilltype: normalizeSkillTypeValue(resolved.skilltype),
          });
        }
        const taxonomy = await ItemService.fetchTaxonomy("arabic");
        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomy));
      } catch (err) {
        setSaveError("Failed to load item or taxonomy.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [itemId, isNew]);

  const directionValue =
    item.Direction?.toUpperCase() === "RTL" ? "RTL" : "LTR";
  const isRtlDirection = directionValue === "RTL";

  // Open Preview for specific option
  const handleOpenPreview = (index) => {
    const sentence = item.Options?.[index]?.Value || "";
    const tokens = extractSentenceTokens(sentence);
    setPreviewOrder(tokens);
    setSelectedOptionIndex(index);
    setPreviewDialogOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setSelectedOptionIndex(null);
    setPreviewOrder([]);
  };

  const handleFieldChange = (key, value) => {
    setItem((p) => ({ ...p, [key]: value }));
  };

  const handleOptionChange = (index, field, value) => {
    setItem((prev) => {
      const opts = [...(prev.Options || [])];
      opts[index] = { ...opts[index], [field]: value };
      return { ...prev, Options: opts };
    });
  };

  const handleAddOption = () => {
    setItem((prev) => ({
      ...prev,
      Options: [...prev.Options, createEmptyOption(prev.Options.length + 1)],
    }));
  };

  const handleRemoveOption = (index) => {
    if (item.Options.length <= 1) return;
    setItem((prev) => {
      const opts = prev.Options.filter((_, i) => i !== index);
      return { ...prev, Options: opts };
    });
  };

  const handleSave = useCallback(async () => {
    if (!item.Name || !item.itemcat || !item.skilltype) {
      setSaveError("Please fill all required fields.");
      return;
    }

    const timestamp = new Date().toISOString();
    const normalizedPoints = Number(item.points) || 1;

    const payload = {
      PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      SK: `item#${itemId || "new"}`,
      createdBy: "",
      updatedBy: "",
      dateCreated: timestamp,
      dateUpdated: timestamp,
      GPK1: "item",
      GSK1: `item#${itemId || "new"}`,
      itemcat: item.itemcat,
      skilltype: item.skilltype,
      Name: item.Name,
      Options: item.Options.map((opt, i) => ({
        id: i + 1,
        Rationale: opt.Rationale || "",
        Value: opt.Value || "",
      })),
      points: normalizedPoints,
      Title:
        item.Title || "<div>Arrange the words to form correct sentence</div>",
      Type: "ReArrange",
      version: "v1",
    };

    try {
      setSaving(true);
      await ItemService.saveItem(payload, isNew ? undefined : itemId);
      setSaveSuccess(true);
      setSaveError(null);
    } catch (err) {
      setSaveError(err?.message || "Failed to save item.");
    } finally {
      setSaving(false);
    }
  }, [item, isNew, itemId]);

  const jsonPreview = useMemo(() => JSON.stringify(item, null, 2), [item]);

  if (loading)
    return (
      <Paper sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading item details...</Typography>
      </Paper>
    );

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="ReArrange" color="secondary" />
              <Typography variant="h5" fontWeight={600}>
                {isNew ? "Create ReArrange Item" : "Edit ReArrange Item"}
              </Typography>
            </Stack>
            <Button variant="outlined" onClick={() => setJsonDialogOpen(true)}>
              View JSON
            </Button>
          </Stack>

          {saveSuccess && (
            <Alert severity="success">Item saved successfully!</Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}

          {/* Fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item Name"
                fullWidth
                required
                value={item.Name}
                onChange={(e) => handleFieldChange("Name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Points"
                type="number"
                fullWidth
                required
                value={item.points}
                onChange={(e) =>
                  handleFieldChange("points", Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth required>
                <InputLabel>Skill Type</InputLabel>
                <Select
                  value={item.skilltype}
                  onChange={(e) =>
                    handleFieldChange(
                      "skilltype",
                      normalizeSkillTypeValue(e.target.value)
                    )
                  }
                >
                  {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Item Category</InputLabel>
                <Select
                  value={item.itemcat}
                  onChange={(e) => handleFieldChange("itemcat", e.target.value)}
                >
                  {taxonomyOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle1" fontWeight={600}>
            Prompt / Question Editor
          </Typography>
          <ReactQuill
            theme="snow"
            value={item.Title}
            onChange={(v) => handleFieldChange("Title", v)}
            style={{ height: "100px", marginBottom: "50px" }}
          />

          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={isRtlDirection}
                onChange={() =>
                  handleFieldChange("Direction", isRtlDirection ? "LTR" : "RTL")
                }
              />
            }
            label="Right-to-left direction"
          />
          <Typography variant="h6" fontWeight={600}>
            Correct Sentence Options
          </Typography>

          {item.Options.map((opt, index) => (
            <Paper
              key={opt.id}
              sx={{ p: 2, mt: 2, backgroundColor: "#fafafa" }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Option {index + 1}
                </Typography>
                {item.Options.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveOption(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Sentence"
                value={opt.Value}
                onChange={(e) =>
                  handleOptionChange(index, "Value", e.target.value)
                }
                sx={{ mt: 1 }}
                inputProps={{
                  dir: isRtlDirection ? "rtl" : "ltr",
                  style: { textAlign: isRtlDirection ? "right" : "left" },
                }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Rationale (optional)"
                value={opt.Rationale}
                onChange={(e) =>
                  handleOptionChange(index, "Rationale", e.target.value)
                }
                sx={{ mt: 2 }}
              />
              <Button
                onClick={() => handleOpenPreview(index)}
                sx={{ mt: 1 }}
                variant="outlined"
              >
                Preview
              </Button>
            </Paper>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddOption}
            sx={{ mt: 2 }}
          >
            Add Another Option
          </Button>

          <Stack direction="row" justifyContent="flex-end">
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Item"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* 🆕 Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Preview Option{" "}
          {selectedOptionIndex !== null ? selectedOptionIndex + 1 : ""}
        </DialogTitle>
        <DialogContent dividers>
          {previewOrder.length ? (
            <DragDropContext
              onDragEnd={(result) => {
                if (!result.destination) return;
                const src = result.source.index;
                const dest = result.destination.index;
                setPreviewOrder((prev) => {
                  const next = [...prev];
                  const [moved] = next.splice(src, 1);
                  next.splice(dest, 0, moved);
                  return next;
                });
              }}
            >
              <Droppable droppableId="preview-tokens" direction="horizontal">
                {(provided) => (
                  <Stack
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{
                      direction: isRtlDirection ? "rtl" : "ltr",
                      justifyContent: isRtlDirection
                        ? "flex-end"
                        : "flex-start",
                      minHeight: 40,
                      mt: 1,
                    }}
                  >
                    {previewOrder.map((token, i) => (
                      <Draggable
                        key={`${token}-${i}`}
                        draggableId={`${token}-${i}`}
                        index={i}
                      >
                        {(dragProvided, snapshot) => (
                          <Box
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            sx={{
                              transform: snapshot.isDragging
                                ? "scale(1.05)"
                                : "none",
                              transition: "transform 100ms linear",
                            }}
                          >
                            <Chip
                              label={token}
                              variant="outlined"
                              sx={{ px: 1.5, py: 0.5 }}
                            />
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <Alert severity="info">No sentence available for preview.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* JSON Dialog */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ReArrangeItemDetail;
